// Coupon status, mirroring website/utils/coupon_status.py.
//
// The backend computes `status` per coupon; this module only labels, orders and
// filters it, so the two cannot drift on what a status *means*.

export const COUPON_STATUS_AVAILABLE = "available";
export const COUPON_STATUS_SENT = "sent";
export const COUPON_STATUS_PARTIALLY_USED = "partially_used";
export const COUPON_STATUS_USED = "used";

/** Unused first, fully used last — matches COUPON_STATUS_SORT_ORDER server-side. */
export const COUPON_STATUS_SORT_ORDER = {
  [COUPON_STATUS_AVAILABLE]: 0,
  [COUPON_STATUS_SENT]: 1,
  [COUPON_STATUS_PARTIALLY_USED]: 2,
  [COUPON_STATUS_USED]: 3,
};

export const COUPON_STATUS_LABELS = {
  [COUPON_STATUS_AVAILABLE]: "Available",
  [COUPON_STATUS_SENT]: "Sent",
  [COUPON_STATUS_PARTIALLY_USED]: "Partially used",
  [COUPON_STATUS_USED]: "Used",
};

/** Options for the status filter, in the same order as the sort. */
export const COUPON_STATUS_FILTER_OPTIONS = [
  COUPON_STATUS_AVAILABLE,
  COUPON_STATUS_SENT,
  COUPON_STATUS_PARTIALLY_USED,
  COUPON_STATUS_USED,
];

/** Status pill colours. Semantic states may use explicit dark variants. */
export const COUPON_STATUS_BADGE_CLASSES = {
  [COUPON_STATUS_AVAILABLE]:
    "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  [COUPON_STATUS_SENT]:
    "bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300",
  [COUPON_STATUS_PARTIALLY_USED]:
    "bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300",
  [COUPON_STATUS_USED]:
    "bg-gray-100 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-dark-200",
};

export const couponStatusLabel = (status) =>
  COUPON_STATUS_LABELS[status] || "Unknown";

export const isUsedStatus = (status) => status === COUPON_STATUS_USED;

/**
 * Sort by status (unused first, used last), then by code so the order is stable.
 * Returns a new array; the input is not mutated.
 */
export const sortCouponsByStatus = (coupons = []) =>
  [...coupons].sort((a, b) => {
    const rankA = COUPON_STATUS_SORT_ORDER[a?.status] ?? 99;
    const rankB = COUPON_STATUS_SORT_ORDER[b?.status] ?? 99;
    if (rankA !== rankB) return rankA - rankB;
    return String(a?.code || "").localeCompare(String(b?.code || ""));
  });

/**
 * Apply the Access Coupons table controls.
 *
 * `includeUsed` hides fully-used coupons entirely; `statuses` (a Set) narrows to an
 * explicit selection. An empty/absent selection means "no status filter", not
 * "match nothing", so the table is never mysteriously blank.
 */
export const filterCoupons = (coupons = [], { includeUsed = true, statuses = null } = {}) =>
  coupons.filter((coupon) => {
    if (!includeUsed && isUsedStatus(coupon?.status)) return false;
    if (statuses && statuses.size > 0 && !statuses.has(coupon?.status)) return false;
    return true;
  });
